import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import AppButton from "../../../common_components/AppButton";
import CustomLoader from "../../../common_components/CustomLoader";
/**
 * Shared geometry for all three buttons — identical size, radius and type, so only
 * the colours tell them apart. Equal widths are what let the centred Development
 * Hrs button line up cleanly over the Proceed / Cancel pair.
 *
 * Values match the dashboard's standard button (`body .MuiButton-containedPrimary`
 * in App.css): 36px tall, 8px radius, 14px/600 capitalised.
 *
 * Every button below is `variant="contained" color="inherit"` ON PURPOSE. The
 * default `color="primary"` produces the `.MuiButton-containedPrimary` class, and
 * that global rule is specificity (0,1,1) — it outranks MUI's `sx` classes (0,1,0)
 * and would override the height, radius, type AND background set here (it forced
 * pink). `containedInherit` carries no global rules, so these styles actually win.
 */
const BUTTON_SX = {
    minWidth: 170,
    height: 36,
    minHeight: 36,
    px: "14px",
    borderRadius: "8px",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    boxShadow: "0px 1px 2px 0px #0A0D120D",
};
/**
 * Generate-Invoice confirmation: a pink "?" badge, the action, then three
 * identically-styled buttons — "+ Development Hrs" centred on its own row (single
 * selection only), with Proceed and Cancel inline beneath it.
 *
 * This is local rather than the shared `ConfirmActionModal` because that one
 * hardcodes a single Cancel + Confirm row; here the actions span two rows.
 */
const GenerateInvoiceModal = ({ open, selectedCount, onProceed, onAddDevelopmentHours, onClose, loading = false, }) => {
    if (!open)
        return null;
    // Development hours are entered against ONE client, so the button is offered
    // only for a single selection; a multi-row batch can just proceed.
    const canAddDevelopmentHours = selectedCount === 1;
    return (_jsx(Dialog, { open: open, maxWidth: "xs", fullWidth: true, 
        // Deliberately no `onClose`: generating an invoice is a committing action,
        // so only the Cancel button dismisses this dialog. Omitting the handler
        // makes a backdrop click a no-op, and `disableEscapeKeyDown` does the
        // same for Esc.
        disableEscapeKeyDown: true, className: "dialog-popup-container", sx: {
            zIndex: 2000,
            "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(3px)",
            },
        }, children: _jsx(DialogContent, { className: "delete-popup-box", children: _jsxs(Box, { textAlign: "center", children: [_jsx(Box, { sx: {
                            width: 88,
                            height: 88,
                            mx: "auto",
                            mb: 2.5,
                            borderRadius: "50%",
                            border: "2px solid #FF008E",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }, children: _jsx(Typography, { sx: { fontSize: "44px", fontWeight: 400, color: "#FF008E", lineHeight: 1, userSelect: "none" }, children: "?" }) }), _jsx(Typography, { sx: { fontSize: "20px", fontWeight: 500, color: "#101828", mb: 1 }, children: "Generate Invoice" }), _jsx(Typography, { sx: { fontSize: "15px", color: "#667085", mb: 3 }, children: "Proceed to Generate Invoice for selected client(s)." }), canAddDevelopmentHours && (_jsx(Box, { sx: { display: "flex", justifyContent: "center", mb: 1.5 }, children: _jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: onAddDevelopmentHours, sx: {
                                ...BUTTON_SX,
                                bgcolor: "#FF008E",
                                color: "#fff",
                                border: "1px solid #FF008E",
                                "&:hover": { bgcolor: "#D10075", border: "1px solid #D10075" },
                            }, children: "+ Development Hrs" }) })), _jsxs(Box, { sx: { display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }, children: [_jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: onProceed, sx: {
                                    ...BUTTON_SX,
                                    bgcolor: "#181D27",
                                    color: "#fff",
                                    border: "1px solid #181D27",
                                    "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                                }, children: loading ? _jsx(CustomLoader, { color: "inherit", size: 18 }) : "Proceed" }), _jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: onClose, sx: {
                                    ...BUTTON_SX,
                                    bgcolor: "#fff",
                                    color: "#414651",
                                    border: "1px solid #D5D7DA",
                                    "&:hover": { bgcolor: "#F9FAFB", border: "1px solid #D5D7DA" },
                                }, children: "Cancel" })] })] }) }) }));
};
export default GenerateInvoiceModal;
